/**
 * Sign-in and access-request screens.
 *
 * The editor is a paid, invitation-run product: there is no public sign-up
 * into the tool itself, only a request that an administrator approves. The
 * panel says that plainly instead of offering a door that does not open.
 */

import { FormEvent, useState } from 'react';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  LogOut,
  ShieldCheck,
  Terminal,
  User,
} from 'lucide-react';
import { useAuth } from './AuthContext';
import { PricingNote } from './PricingNote';
import { ApiError } from './api';

type Mode = 'login' | 'register';

export function LoginScreen(): JSX.Element {
  const { login, register } = useAuth();
  const [mode, setMode] = useState<Mode>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [note, setNote] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      if (mode === 'login') {
        await login(username.trim(), password);
      } else {
        const message = await register(username.trim(), password, note.trim());
        setNotice(message);
        setMode('login');
        setPassword('');
      }
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught.message);
      } else {
        setError('Could not reach the server. Check the connection and try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
    setNotice(null);
  };

  return (
    <div className="auth-shell">
      <section className="auth-hero">
        <div className="auth-brand">
          <img src="/favicon.png" alt="" width={38} height={38} />
          <div>
            <strong>Pixansi</strong>
            <span>create · edit · export</span>
          </div>
        </div>

        <div className="auth-copy">
          <h1>Terminal interfaces, drawn by hand.</h1>
          <p>
            A visual editor for ANSI art, TUI layouts, dashboards and splash screens. Every cell is a
            character with a colour; ANSI is an export, not the source.
          </p>
        </div>

        <div className="auth-terminal" aria-hidden="true">
          <div className="auth-term-head">
            <span>PIXANSI</span>
            <span className="live">live</span>
          </div>
          <div className="auth-term-label">SYSTEM</div>
          <div className="auth-term-row">
            <span>cpu</span>
            <b>{'{{cpu}}'}</b>
          </div>
          <div className="auth-term-row">
            <span>ram</span>
            <b>{'{{ram}}'}</b>
          </div>
          <div className="auth-term-row">
            <span>uptime</span>
            <b>{'{{uptime}}'}</b>
          </div>
          <div className="auth-term-rule" />
          <div className="auth-term-label">USERS</div>
          <div className="auth-term-row">
            <span>online</span>
            <b>{'{{connected_users}}'}</b>
          </div>
          <div className="auth-term-row">
            <span>peak</span>
            <b>{'{{peak_users}}'}</b>
          </div>
          <div className="auth-term-rule" />
          <div className="auth-term-row">
            <span>status</span>
            <b>{'{{status}}'}</b>
          </div>
          <div className="auth-term-cursor">pixansi --new</div>
        </div>

        <ul className="auth-features">
          <li>
            <Terminal size={14} /> ANSI parser, encoder and TrueColor export
          </li>
          <li>
            <ShieldCheck size={14} /> Accounts, plans and cloud projects
          </li>
        </ul>
      </section>

      <section className="auth-panel">
        <div className="auth-card">
          <PricingNote />
          <div className="auth-tabs" role="tablist" aria-label="Account">
            <button
              role="tab"
              aria-selected={mode === 'login'}
              className={mode === 'login' ? 'active' : ''}
              onClick={() => switchMode('login')}
            >
              Sign in
            </button>
            <button
              role="tab"
              aria-selected={mode === 'register'}
              className={mode === 'register' ? 'active' : ''}
              onClick={() => switchMode('register')}
            >
              Request access
            </button>
          </div>

          <form onSubmit={submit} className="auth-form">
            <label className="auth-field">
              <span>Username</span>
              <span className="auth-input">
                <User size={14} />
                <input
                  autoFocus
                  autoComplete="username"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  placeholder="your handle"
                  required
                  minLength={3}
                  maxLength={32}
                />
              </span>
            </label>

            <label className="auth-field">
              <span>Password</span>
              <span className="auth-input">
                <KeyRound size={14} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder={mode === 'login' ? 'your password' : 'at least 8 characters'}
                  required
                  minLength={mode === 'login' ? 1 : 8}
                  maxLength={256}
                />
                <button
                  type="button"
                  className="auth-eye"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((value) => !value)}
                >
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </span>
            </label>

            {mode === 'register' ? (
              <label className="auth-field">
                <span>Discord handle / note for the administrator</span>
                <textarea
                  className="auth-note"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder="Your Discord handle, what you are building, anything that helps match your payment."
                  maxLength={500}
                  rows={3}
                />
              </label>
            ) : null}

            {error ? (
              <p className="auth-message error">
                <AlertCircle size={14} /> {error}
              </p>
            ) : null}
            {notice ? (
              <p className="auth-message success">
                <CheckCircle2 size={14} /> {notice}
              </p>
            ) : null}

            <button className="btn primary auth-submit" type="submit" disabled={busy}>
              {busy ? <Loader2 size={15} className="spin" /> : <ArrowRight size={15} />}
              {mode === 'login' ? 'Sign in' : 'Request an account'}
            </button>
          </form>

          <footer className="auth-foot">
            <p>
              {mode === 'login'
                ? 'Accounts are manual: request access, then arrange the weekly crypto payment on Discord. There is no checkout on this site.'
                : 'After this request, DM us on Discord to arrange the $15 weekly crypto payment. The account activates once payment is confirmed.'}
            </p>
          </footer>
        </div>

        <p className="auth-legal">Pixansi · private deployment</p>
      </section>
    </div>
  );
}

/** Shown when a valid session belongs to an account that may not work today. */
export function AccessBlocked(): JSX.Element {
  const { user, access, logout } = useAuth();
  const code = access?.code ?? 'BLOCKED';

  return (
    <div className="auth-shell auth-shell-blocked">
      <section className="auth-panel">
        <div className="auth-card">
          <div className="auth-blocked-mark">
            <ShieldCheck size={26} />
          </div>
          <h2>{code === 'EXPIRED' ? 'Subscription expired' : 'Account unavailable'}</h2>
          <p className="auth-blocked-text">
            {access?.message ?? 'This account cannot open the editor right now.'}
          </p>
          <dl className="auth-facts">
            <div>
              <dt>Account</dt>
              <dd>{user?.username}</dd>
            </div>
            <div>
              <dt>Plan</dt>
              <dd>{user?.plan ?? '—'}</dd>
            </div>
          </dl>
          <PricingNote />
          <button className="btn auth-submit" onClick={() => void logout()}>
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </section>
    </div>
  );
}

/** Full-window loading state while the session is being checked. */
export function BootSplash(): JSX.Element {
  return (
    <div className="auth-shell auth-shell-blocked">
      <div className="auth-boot">
        <img src="/favicon.png" alt="" width={44} height={44} />
        <Loader2 size={18} className="spin" />
      </div>
    </div>
  );
}
